import * as THREE from 'three';
import { settings } from '@/config';
import type { MaterialManager } from '@/materials/MaterialManager';

/**
 * Procedural fallback scene used when no GLB is present, so the entire pipeline
 * (renderer, color management, lighting, shadows, scroll-camera, raycaster,
 * performance monitor) is verifiable immediately. Includes:
 *  - a shadow-catching ground plane
 *  - an UNLIT baked cube with a UV/orientation checker (validates sRGB, flipY,
 *    mipmaps, anisotropy and UV orientation)
 *  - a PBR sphere (validates lighting + shadows + tone mapping)
 *  - an emissive marker (validates the emissive / future-bloom workflow)
 */
export class Placeholder {
  readonly group = new THREE.Group();

  constructor(materials: MaterialManager) {
    const size = settings.world.placeholder.groundSize;
    this.group.name = 'placeholder';

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(size, size),
      new THREE.MeshStandardMaterial({ color: 0x14141a, roughness: 0.95, metalness: 0 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    ground.name = 'placeholder_ground';

    const baked = new THREE.Mesh(
      new THREE.BoxGeometry(3, 3, 3),
      materials.createBaked(createCheckerTexture()),
    );
    baked.position.set(-3, 1.5, 0);
    baked.castShadow = true;
    baked.receiveShadow = true;
    baked.name = 'placeholder_baked';

    const pbr = new THREE.Mesh(
      new THREE.SphereGeometry(1.5, 64, 64),
      new THREE.MeshStandardMaterial({ color: 0x8b9cff, roughness: 0.25, metalness: 0.6 }),
    );
    pbr.position.set(3, 1.5, 0);
    pbr.castShadow = true;
    pbr.receiveShadow = true;
    pbr.name = 'placeholder_pbr';

    const emissive = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.6, 0),
      new THREE.MeshStandardMaterial({
        color: 0x000000,
        emissive: new THREE.Color(0x6c5cff),
        emissiveIntensity: 2,
      }),
    );
    emissive.position.set(0, 0.6, 3);
    emissive.name = 'placeholder_emissive';

    this.group.add(ground, baked, pbr, emissive);
  }

  /** Objects exposed to the raycaster (everything except the ground). */
  get interactive(): THREE.Object3D[] {
    return this.group.children.filter((child) => child.name !== 'placeholder_ground');
  }

  dispose(): void {
    this.group.traverse((object) => {
      const mesh = object as THREE.Mesh;
      mesh.geometry?.dispose();
      const material = mesh.material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
  }
}

/** Build a UV/orientation checker texture (top-left = red, bottom-right = green). */
function createCheckerTexture(): THREE.Texture {
  const resolution = 512;
  const canvas = document.createElement('canvas');
  canvas.width = resolution;
  canvas.height = resolution;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable for checker texture');

  const cells = 8;
  const step = resolution / cells;
  for (let y = 0; y < cells; y += 1) {
    for (let x = 0; x < cells; x += 1) {
      ctx.fillStyle = (x + y) % 2 === 0 ? '#2a2f4a' : '#cdd6ff';
      ctx.fillRect(x * step, y * step, step, step);
    }
  }
  ctx.fillStyle = '#ff5c5c';
  ctx.fillRect(0, 0, step, step);
  ctx.fillStyle = '#5cff8f';
  ctx.fillRect(resolution - step, resolution - step, step, step);

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  return texture;
}
